import type { ApiErrorBody } from './types'

/**
 * Единственное место, где приложение ходит в сеть.
 *
 * Всё остальное — сторы и экраны — работает через ручки из `endpoints`,
 * а те через `request`. Один вход означает: заголовок авторизации
 * ставится один раз, конверт ошибки разбирается один раз, и истечение
 * сессии обрабатывается одинаково везде, а не двадцатью способами.
 */

export class ApiError extends Error {
  readonly status: number
  readonly code: string
  readonly details: Record<string, unknown>
  /** Сколько секунд просил подождать сервер (заголовок Retry-After). */
  readonly retryAfter: number | null

  constructor(
    status: number,
    code: string,
    message: string,
    details: Record<string, unknown>,
    retryAfter: number | null,
  ) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.code = code
    this.details = details
    this.retryAfter = retryAfter
  }
}

/** Сервер не ответил вовсе. Отличать от ApiError обязательно: при отказе
 *  сети данные на экране остаются верными, при ошибке сервера — нет. */
export class NetworkError extends Error {
  constructor(cause: unknown) {
    super('Сервер не отвечает')
    this.name = 'NetworkError'
    this.cause = cause
  }
}

// ---------------------------------------------------------------- сессия

let accessToken: string | null = null
let expiredHandler: (() => void) | null = null

export function setAccessToken(token: string | null): void {
  accessToken = token
}

/**
 * Что делать, когда сервер сказал 401 на запрос С токеном.
 *
 * Токен живёт пятнадцать минут, ручки обновления у бекенда нет — значит
 * сессия обрывается посреди работы, и обработать это надо в одном месте,
 * а не в каждом экране. Обработчик ставит стор авторизации; сюда он
 * передаётся функцией, чтобы модуль сети не знал про сторы и не
 * образовалась circular import.
 */
export function onSessionExpired(handler: (() => void) | null): void {
  expiredHandler = handler
}

// ------------------------------------------------------------ часы сервера

/**
 * Расхождение часов между устройством и сервером, в миллисекундах.
 *
 * Таймер до истечения заявки считается от `expires_at` с сервера,
 * а «сейчас» берётся с устройства. Часы на телефонах уходят: сдвига
 * в несколько минут достаточно, чтобы живая заявка показала «время
 * вышло», и покупатель ушёл. Точное время приходит в заголовке `Date`
 * каждого ответа — запоминаем разницу и дальше считаем от неё.
 */
let clockSkewMs = 0

export function serverNow(): number {
  return Date.now() + clockSkewMs
}

function rememberClockSkew(response: Response): void {
  const header = response.headers.get('date')
  if (!header) return
  const serverTime = Date.parse(header)
  if (Number.isNaN(serverTime)) return
  clockSkewMs = serverTime - Date.now()
}

// ------------------------------------------------------------------ запрос

function parseRetryAfter(response: Response): number | null {
  const raw = response.headers.get('retry-after')
  if (!raw) return null
  const seconds = Number.parseInt(raw, 10)
  return Number.isFinite(seconds) ? seconds : null
}

async function readError(response: Response): Promise<ApiError> {
  let code = `http_${response.status}`
  let message = 'Что-то пошло не так'
  let details: Record<string, unknown> = {}
  try {
    const body = (await response.json()) as Partial<ApiErrorBody>
    if (body.error) {
      code = body.error.code || code
      message = body.error.message || message
      details = body.error.details ?? {}
    }
  } catch {
    // Тело не json — например, 502 от nginx. Код по статусу честнее
    // выдуманного текста.
  }
  return new ApiError(response.status, code, message, details, parseRetryAfter(response))
}

/** Значения, которые умеют превращаться в параметр строки запроса. */
export type QueryValue = string | number | boolean | undefined | null
export type QueryParams = { [key: string]: QueryValue }

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE'
  /** Тело запроса. Сериализуется в JSON. */
  body?: unknown
  /**
   * Параметры строки запроса. `undefined`, `null` и пустая строка
   * не отправляются — иначе пустой фильтр уходит на сервер как
   * `?status=` и часть бекендов понимает это как «статус пустой».
   *
   * Тип намеренно не `Record<...>`: интерфейсы фильтров (например
   * HistoryFilter) не имеют индексной сигнатуры и под Record не
   * подходят, а переписывать каждый фильтр в Record — потерять имена
   * полей и проверку опечаток.
   */
  query?: QueryParams
  headers?: Record<string, string>
  signal?: AbortSignal | null
  /** Публичная ручка: токен не ставим и на 401 сессию не сбрасываем. */
  anonymous?: boolean
}

const BASE = '/api/v1'

/*
  Уже летящие GET-запросы.

  В режиме разработки React в StrictMode монтирует компонент дважды,
  и каждый эффект загрузки срабатывает по два раза: одиннадцать
  запросов обзора превращались в двадцать два. Браузер держит к одному
  хосту шесть соединений — остальные вставали в очередь, и вместе
  с ними загрузка шрифта, поэтому страница сначала показывалась
  чужим начертанием, а цифры доезжали через несколько секунд.

  Дедупликация не кеш: обещание живёт РОВНО пока летит запрос и
  выбрасывается сразу после. Второй вызов получает тот же ответ,
  что и первый, а не старые данные.

  Только GET. Повторный POST или PATCH — это отдельное действие
  с деньгами, и склеивать два таких вызова в один нельзя ни при
  каких условиях: за идемпотентность там отвечает Idempotency-Key,
  а не совпадение адреса.
*/
const inFlight = new Map<string, Promise<unknown>>()

export function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  if ((options.method ?? 'GET') !== 'GET') return send<T>(path, options)

  const key = JSON.stringify([path, options.query ?? null, options.anonymous ?? false])
  const running = inFlight.get(key)
  if (running) return running as Promise<T>

  const promise = send<T>(path, options).finally(() => { inFlight.delete(key) })
  inFlight.set(key, promise)
  return promise
}

async function send<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, query, headers, signal, anonymous } = options

  const url = new URL(`${BASE}${path}`, window.location.origin)
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined && value !== null && value !== '') {
      url.searchParams.set(key, String(value))
    }
  }

  const authed = !anonymous && accessToken !== null
  const init: RequestInit = {
    method,
    headers: {
      Accept: 'application/json',
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...(authed ? { Authorization: `Bearer ${accessToken}` } : {}),
      ...headers,
    },
    signal: signal ?? null,
  }
  if (body !== undefined) init.body = JSON.stringify(body)

  let response: Response
  try {
    response = await fetch(url.toString(), init)
  } catch (cause) {
    // AbortError — наша собственная отмена, а не отказ сети: пробрасываем
    // как есть, иначе уход со страницы выглядит как сбой.
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause
    throw new NetworkError(cause)
  }

  rememberClockSkew(response)

  if (response.status === 401 && authed) {
    // Сессия истекла именно у нас, а не «ручка требует входа».
    expiredHandler?.()
  }

  if (!response.ok) throw await readError(response)

  // 204 и пустое тело — валидный ответ, JSON.parse на нём падает.
  if (response.status === 204) return undefined as T
  const text = await response.text()
  return (text ? JSON.parse(text) : undefined) as T
}
